import type { CSSProperties } from 'react';
import { Field } from '../../components/Field';
import { DatePicker } from '../../components/DatePicker';
import { TimeSelect } from '../../components/TimeSelect';
import { PS, T } from '../../lib/theme';
import {
  LOCALE_LABELS,
  PAYMENT_MODE_LABELS,
  TOURNAMENT_LOCALES,
  TTAR_RESULT_INPUT_MODE_LABELS,
} from '../../lib/tournaments';
import type { TournamentFormValue } from '../../lib/tournament-form';
import type { TableEmbed, TournamentLocale } from '../../lib/types';

interface Props {
  value: TournamentFormValue;
  onChange: (patch: Partial<TournamentFormValue>) => void;
  tables: TableEmbed[];
  tablesLoading: boolean;
  /**
   * Disables the schedule/table/fee/deadline controls with a hint that they
   * are frozen once a participant has confirmed. This is a UX hint derived
   * from the roster the caller already fetched — the API is what actually
   * enforces the freeze (a stale hint just means the PATCH comes back with a
   * 409 `frozen_field`, which the caller must still surface).
   */
  frozenHint?: boolean;
}

/**
 * The field set shared by "create tournament" and "edit tournament": three
 * locale sections (title + description), schedule, table multi-select, fee,
 * capacity, registration deadline, image. One definition so create and edit
 * can never drift on what a tournament's editable surface looks like.
 */
export function TournamentFieldsForm({ value, onChange, tables, tablesLoading, frozenHint }: Props) {
  const setTranslation = (locale: TournamentLocale, field: 'title' | 'description', v: string) => {
    onChange({
      translations: {
        ...value.translations,
        [locale]: { ...value.translations[locale], [field]: v },
      },
    });
  };

  const toggleTable = (id: number) => {
    const next = value.tableIds.includes(id)
      ? value.tableIds.filter((t) => t !== id)
      : [...value.tableIds, id];
    onChange({ tableIds: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Localized content</h2>
        {TOURNAMENT_LOCALES.map((locale) => (
          <div
            key={locale}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 4,
              padding: 12,
            }}
          >
            <span style={{ ...T.label }}>{LOCALE_LABELS[locale]}</span>
            <Field label="Title">
              <input
                type="text"
                value={value.translations[locale].title}
                onChange={(e) => setTranslation(locale, 'title', e.target.value)}
                style={inputStyle}
                aria-label={`${LOCALE_LABELS[locale]} title`}
              />
            </Field>
            <Field label="Description (optional)">
              <textarea
                value={value.translations[locale].description}
                onChange={(e) => setTranslation(locale, 'description', e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' as const }}
                aria-label={`${LOCALE_LABELS[locale]} description`}
              />
            </Field>
          </div>
        ))}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Schedule &amp; tables</h2>
        {frozenHint && (
          <p style={{ ...T.caption, color: PS.textSec, margin: 0 }} role="status">
            Frozen after the first confirmed participant — schedule, tables, fee, payment mode and
            registration deadline can no longer be changed. Localized content, image and capacity
            increases still can.
          </p>
        )}
        {/* A native <fieldset disabled> cascades to every descendant control,
            including the ones inside DatePicker/TimeSelect — no need to thread
            a `disabled` prop through those shared components for one caller. */}
        <fieldset disabled={frozenHint} style={fieldsetStyle}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Date">
              <DatePicker
                value={value.date}
                onChange={(v) => onChange({ date: v })}
                ariaLabel="Date"
              />
            </Field>
            <Field label="Start time">
              <TimeSelect
                value={value.startTime}
                onChange={(v) => onChange({ startTime: v })}
                min="00:00"
                max="24:00"
                ariaLabel="Start time"
              />
            </Field>
            <Field label="End time">
              <TimeSelect
                value={value.endTime}
                onChange={(v) => onChange({ endTime: v })}
                min="00:00"
                max="24:00"
                includeMax
                ariaLabel="End time"
              />
            </Field>
            <Field label="Warmup (min)">
              <input
                type="number"
                min={0}
                max={120}
                step={5}
                value={value.warmupMinutes}
                onChange={(e) => onChange({ warmupMinutes: e.target.value })}
                style={inputStyle}
                aria-label="Warmup (min)"
              />
            </Field>
          </div>

          <Field label="Tables">
            {tablesLoading ? (
              <span style={{ ...T.caption, color: PS.textSec }}>Loading tables…</span>
            ) : tables.length === 0 ? (
              <span style={{ ...T.caption, color: PS.textSec }}>No tables found.</span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {tables.map((t) => (
                  <label
                    key={t.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      border: `1px solid ${PS.border}`,
                      borderRadius: 3,
                      ...T.ui,
                      minHeight: 40,
                      boxSizing: 'border-box',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={value.tableIds.includes(t.id)}
                      onChange={() => toggleTable(t.id)}
                    />
                    {t.name}
                  </label>
                ))}
              </div>
            )}
          </Field>
        </fieldset>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Fee, capacity &amp; registration</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <fieldset disabled={frozenHint} style={fieldsetStyle}>
            <Field label="Fee (GEL)">
              <input
                type="number"
                min={0}
                step="0.01"
                value={value.feeGel}
                onChange={(e) => onChange({ feeGel: e.target.value })}
                style={inputStyle}
                aria-label="Fee in GEL"
              />
            </Field>
            {Number(value.feeGel) > 0 && (
              <Field label="Payment">
                <select
                  value={value.paymentMode}
                  onChange={(e) => onChange({ paymentMode: e.target.value as typeof value.paymentMode })}
                  style={inputStyle}
                  aria-label="Payment"
                >
                  {Object.entries(PAYMENT_MODE_LABELS).map(([mode, label]) => (
                    <option key={mode} value={mode}>{label}</option>
                  ))}
                </select>
                <span style={{ ...T.caption, color: PS.textSec }}>
                  Pay later: the seat is confirmed at registration and the player pays from their tournament page or in cash at the club. Locked once a participant has confirmed.
                </span>
              </Field>
            )}
          </fieldset>
          {/* Capacity is never disabled here: increases stay allowed even
              once frozen. A decrease is refused by the API — enforced there,
              not pre-empted by this form. */}
          <Field label="Capacity (optional)">
            <input
              type="number"
              min={1}
              step={1}
              value={value.maxParticipants}
              onChange={(e) => onChange({ maxParticipants: e.target.value })}
              style={inputStyle}
              aria-label="Capacity"
              placeholder="Unlimited"
            />
          </Field>
          {/* Mirrored to TTAR as `minParticipants` (TTAR needs one to publish);
              not enforced by ProSpin itself. Blank sends TTAR's floor of 2. */}
          <Field label="Min participants (TTAR)">
            <input
              type="number"
              min={2}
              max={255}
              step={1}
              value={value.minParticipants}
              onChange={(e) => onChange({ minParticipants: e.target.value })}
              style={inputStyle}
              aria-label="Min participants"
              placeholder="2"
            />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="TTAR rating from">
            <input
              type="number"
              min={1}
              step={1}
              value={value.minTtarRating}
              onChange={(e) => onChange({ minTtarRating: e.target.value })}
              style={inputStyle}
              aria-label="TTAR rating from"
              placeholder="Any"
            />
          </Field>
          <Field label="TTAR rating to">
            <input
              type="number"
              min={1}
              step={1}
              value={value.maxTtarRating}
              onChange={(e) => onChange({ maxTtarRating: e.target.value })}
              style={inputStyle}
              aria-label="TTAR rating to"
              placeholder="Any"
            />
          </Field>
        </div>
        <Field label="TTAR results">
          <select
            value={value.ttarResultInputMode}
            onChange={(e) => onChange({ ttarResultInputMode: e.target.value as typeof value.ttarResultInputMode })}
            style={inputStyle}
            aria-label="TTAR results"
          >
            {Object.entries(TTAR_RESULT_INPUT_MODE_LABELS).map(([mode, label]) => (
              <option key={mode} value={mode}>{label}</option>
            ))}
          </select>
          <span style={{ ...T.caption, color: PS.textSec }}>
            Applies when the tournament is created in TTAR.
          </span>
        </Field>
        {/* Never frozen: the switch only gates new registrations, so staff
            can close or reopen cash at any point (api migrations/025). */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, ...T.ui }}>
          <input
            type="checkbox"
            checked={value.cashDisabled}
            onChange={(e) => onChange({ cashDisabled: e.target.checked })}
            aria-label="Disable cash payment"
          />
          Disable cash payment (card only)
        </label>
        <fieldset disabled={frozenHint} style={fieldsetStyle}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Registration closes — date (optional)">
              <DatePicker
                value={value.registrationClosesDate}
                onChange={(v) => onChange({ registrationClosesDate: v })}
                ariaLabel="Registration closes date"
              />
            </Field>
            {/* No `includeMax` here: unlike start/end time this becomes an ISO
                instant (registrationClosesIso in lib/tournament-form.ts), and
                hour 24 isn't valid ISO — the default max="24:00" with
                includeMax off already stops one step short, at 23:45. */}
            <Field label="Registration closes — time">
              <TimeSelect
                value={value.registrationClosesTime}
                onChange={(v) => onChange({ registrationClosesTime: v })}
                min="00:00"
                ariaLabel="Registration closes time"
              />
            </Field>
          </div>
        </fieldset>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Image</h2>
        <Field label="Image URL (optional)">
          <input
            type="text"
            value={value.imageUrl}
            onChange={(e) => onChange({ imageUrl: e.target.value })}
            style={inputStyle}
            placeholder="https://…"
            aria-label="Image URL"
          />
        </Field>
      </section>
    </div>
  );
}

const fieldsetStyle: CSSProperties = {
  border: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minInlineSize: 'auto',
};

const inputStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  background: PS.white,
  boxSizing: 'border-box',
  width: '100%',
};
